import { Button, Col, Row, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import api from '../Service';
import '../style.scss';
import {
  AllergyMaster,
  AllergyItem,
  NutritionMaster,
  PurchaseCalorieSetting,
  PurchaseCalorieSettingRequest,
  PurchaseNutritionMaster,
} from '../models/PurchaseProcessedCalorieAllergySetting';
import {
  ITEMS_EQUIVALENT_TO_SPECIFIC_RAW_MATERIALS,
  SPECIFIC_RAW_MATERIALS,
  PENDING_CONFIRMATION,
  PENDING_APPROVAL,
  APPROVED,
  ALLERGY_ITEM_DEFAULT_CD,
  ALLERGY_ITEM_DEFAULT_NAME,
} from '../constant';
import GroupButton, { HandleButtonClickParam } from '../components/ButtonApproveGroup';
import NutritionInputItem from '../components/InputItem';
import { toast } from 'react-toastify';
import { ApiError, ApiResponse } from 'models';
import { AxiosError } from 'axios';
import AllergyTable from '../components/AllergyTable';
import { uniqueId } from 'lodash';
import * as yup from 'yup';

const COLUMN_WIDTH = [10.375, 10.375, 10.375, 10.375, 10.375, 10.375];
type NutritionError = {
  [key: string]: {
    nutrition_amount: string;
  };
};

function PurchaseProcessedCalorieAllergyDetailSetting() {
  const { t } = useTranslation();
  const { sms_cd }: { sms_cd: string } = useParams();
  const history = useHistory();

  //state
  const [data, setData] = useState<PurchaseCalorieSetting>();
  const [nutritions, setNutritions] = useState<NutritionMaster[]>([]);
  const [allergyUnitItems, setAllergyUnitItems] = useState<AllergyItem[]>([]);
  const [allergyMasterList, setAllergyMasterList] = useState<AllergyMaster[]>([]);

  const [status, setStatus] = useState('');
  const [lastUpdater, setLastUpdater] = useState('');
  const [errors, setErrors] = useState<NutritionError>({});

  //schema
  const nutritionSchema = yup.array().of(
    yup.object().shape({
      nutrition_amount: yup
        .number()
        .positive(t('CalorieAllergySettingDetail.validation.positive'))
        .min(0, t('CalorieAllergySettingDetail.validation.min'))
        .required(t('CalorieAllergySettingDetail.validation.required'))
        .typeError(t('CalorieAllergySettingDetail.validation.number')),
    })
  );

  // constant
  const apiCalls = {
    [HandleButtonClickParam.CONFIRMATION_REQUEST]: api.confirmRequest,
    [HandleButtonClickParam.APPROVAL_REQUEST]: api.approveRequest,
    [HandleButtonClickParam.ADMIT]: api.admitRequest,
    [HandleButtonClickParam.REMAND]: api.cancelRequest,
  };

  //useEffect
  useEffect(() => {
    fetchData().then(() => {
      if (nutritions.length === 0) {
        fetchNutritions();
      }
      if (allergyUnitItems.length === 0) {
        fetchAllergyUnits();
      }
      if (allergyMasterList.length === 0) {
        fetchAllergyMaster();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    validateInputFields(data?.purchase_nutrition_master || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (data && allergyMasterList.length > 0) {
      allergyMasterList.forEach((item) => {
        const isExist = data.purchase_item_allergy_master.find((p) => p.allergy_master.allergy_cd === item.allergy_cd);
        if (!isExist) {
          data.purchase_item_allergy_master.push({
            id: uniqueId('allergy-master-'),
            allergy_master: item,
            allergy_item_master: {
              allergy_item_cd: ALLERGY_ITEM_DEFAULT_CD,
              allergy_item_name: ALLERGY_ITEM_DEFAULT_NAME,
            },
            change_flg: false,
            add_new_flg: true,
          });
        }
      });
      setData({ ...data });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allergyMasterList.length]);

  useEffect(() => {
    if (data && nutritions.length > 0) {
      nutritions.forEach((item) => {
        const isExist = data.purchase_nutrition_master.find((p) => p.nutrition_cd === item.nutrition_cd);
        if (!isExist) {
          data.purchase_nutrition_master.push({
            id: uniqueId('nutrition-master-'),
            nutrition_amount: null,
            nutrition_cd: item.nutrition_cd,
            change_flg: false,
            add_new_flg: true,
          });
        }
      });
      setData({ ...data });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nutritions.length]);

  // function
  const fetchNutritions = async () => {
    try {
      const res = await api.getAllNutritions();
      setNutritions(res);
    } catch (error) {
      console.error('Error fetching data: ', error);
    }
  };

  const fetchAllergyUnits = async () => {
    try {
      const res = await api.getAllItemUnitAllergies();
      setAllergyUnitItems(res);
    } catch (error) {
      console.error('Error fetching data: ', error);
    }
  };

  const fetchAllergyMaster = async () => {
    try {
      const res = await api.getAllItemMasterAllergies();
      setAllergyMasterList(res);
    } catch (error) {
      console.error('Error fetching data: ', error);
    }
  };

  const fetchData = async () => {
    try {
      const response = await api.getById(sms_cd);
      setStatus(response.status);
      if (response.status === PENDING_CONFIRMATION) {
        setLastUpdater(response.applicant_cd);
      } else if (response.status === PENDING_APPROVAL) {
        setLastUpdater(response.verifier_cd);
      }
      setData(response);
      fetchNutritions();
    } catch (error) {
      console.error('Error fetching data: ', error);
    }
  };

  const confirmRequestStatus = async (
    data: PurchaseCalorieSettingRequest,
    callback: (data: PurchaseCalorieSettingRequest) => Promise<ApiResponse>
  ) => {
    try {
      const response = await callback(data);
      if (response) {
        toast.success(response.message);
        fetchData();
      }
    } catch (error) {
      console.error('Error fetching data: ', error);
      const res = (error as AxiosError<ApiError>)?.response?.data;
      const status = (error as AxiosError<ApiError>)?.response?.status;
      if (res && res.message && status !== 500) {
        toast.error(res.message);
      }
    }
  };

  const approveRequestStatus = async (
    data: { sms_cd: string },
    callback: (data: { sms_cd: string }) => Promise<ApiResponse>
  ) => {
    try {
      const response = await callback(data);
      if (response) {
        toast.success(response.message);
        fetchData();
      }
    } catch (error) {
      console.error('Error fetching data: ', error);
      const res = (error as AxiosError<ApiError>)?.response?.data;
      if (res) {
        toast.error(res.message);
      }
    }
  };

  const handleApproveButtonClick = async (buttonType: HandleButtonClickParam) => {
    if (!data) return;
    const dataUpdate = {
      supplier_item_cd: data.supplier_item_cd,
      status: status,
      remarks: data.process_item ? data.process_item.remarks : data.purchase_item.remarks,
      purchase_nutrition_master: data.purchase_nutrition_master
        .filter((p) => p.change_flg || p.add_new_flg)
        .map((p) => ({
          id: p.add_new_flg ? '0' : p.id,
          nutrition_amount: p.nutrition_amount,
          nutrition_cd: p.nutrition_cd,
          change_flg: p.change_flg,
        })),
      purchase_item_allergy_master: data.purchase_item_allergy_master
        .filter((p) => p.change_flg || p.add_new_flg)
        .map((p) => ({
          id: p.add_new_flg ? '0' : p.id,
          allergy_master: p.allergy_master,
          allergy_item_master: p.allergy_item_master,
          change_flg: p.change_flg,
        })),
    };

    const new_errors = await validateInputFields(data?.purchase_nutrition_master || []);
    if (Object.keys(new_errors).length !== 0) {
      return;
    }

    if (buttonType === HandleButtonClickParam.CONFIRMATION_REQUEST) {
      const apiCall = apiCalls[buttonType];
      confirmRequestStatus(dataUpdate, async () => await apiCall(dataUpdate));
    } else {
      const apiCall = apiCalls[buttonType];
      approveRequestStatus(
        { sms_cd: data.supplier_item_cd },
        async () => await apiCall({ sms_cd: data.supplier_item_cd })
      );
    }
  };

  // handle change nutrition
  const handleChangeNutritionAmount = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;
    const updateDate = data && {
      ...data,
      purchase_nutrition_master: data.purchase_nutrition_master.map((nutrition) =>
        nutrition.id.toString() === id
          ? { ...nutrition, nutrition_amount: parseFloat(value), change_flg: true }
          : nutrition
      ),
    };
    setData(updateDate);
  };

  const handleChangeAllergyItem = (event: React.ChangeEvent<HTMLSelectElement>, allergyMaster: AllergyMaster) => {
    const { id, value } = event.target;
    const allergy_item_cd = Number(value);
    const allergy_item = allergyUnitItems.find((allergy) => allergy.allergy_item_cd === allergy_item_cd)!;
    setData(
      data && {
        ...data,
        purchase_item_allergy_master: data.purchase_item_allergy_master.map((purchased_allergy) =>
          purchased_allergy.id.toString() === id
            ? {
                ...purchased_allergy,
                change_flg: true,
                allergy_item_master: {
                  allergy_item_cd: allergy_item.allergy_item_cd,
                  allergy_item_name: allergy_item.allergy_item_name,
                },
              }
            : purchased_allergy
        ),
      }
    );
  };

  const handleChangeRemark = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setData(
      data && {
        ...data,
        [data.process_item ? 'process_item' : 'purchase_item']: {
          ...(data.process_item ? data.process_item : data.purchase_item),
          remarks: event.target.value,
        },
      }
    );
  };

  const validateInputFields = async (_nutritons: PurchaseNutritionMaster[]) => {
    const newErrors: NutritionError = {};
    try {
      await nutritionSchema.validate(_nutritons, {
        abortEarly: false,
      });
    } catch (error: yup.ValidationError | any) {
      error.inner.forEach((err: { path: string; message: any }) => {
        const match = err.path.match(/\[(\d+)\]\.([a-zA-Z_][a-zA-Z_0-9]*)/);
        if (match) {
          const idx = match[1];
          const key = match[2];
          const nutrition = _nutritons[parseInt(idx, 10)];
          newErrors[nutrition.nutrition_cd] = {
            ...errors[nutrition.nutrition_cd],
            [key]: err.message,
          };
        }
      });
    }
    setErrors(newErrors);
    return newErrors;
  };

  return (
    <div className="page-content d-flex flex-column">
      {/* header */}
      <div className="pb-3 border-bottom">
        <Row className="align-items-center">
          <Col xs="4">
            <h4 className="mb-0 fw-bold">{t('CalorieAllergySettingDetail.head_title')}</h4>
          </Col>
          <Col xs="8" className="d-flex justify-content-end">
            <Stack direction="horizontal" gap={4}>
              <GroupButton status={status} lastUpdater={lastUpdater} handleButtonClick={handleApproveButtonClick} />
              <Button
                className="fs-5 fw-bold btn-sm"
                onClick={() => history.push('/calorie-management/purchase-processed-calorie-allergy-setting')}
              >
                {t('CalorieAllergySettingDetail.go_back')}
              </Button>
            </Stack>
          </Col>
        </Row>
      </div>

      {/* filter */}
      <div className="container-xxxl mt-3 align-items-center">
        <div className="align-items-center mb-5">
          <div className="card border-warning bg-main">
            <div className="card-body text-start">
              <div className="row py-1">
                <div className="col">
                  {t('CalorieAllergySettingDetail.sms_code')}: {data?.supplier_item_cd}
                </div>
                <div className="col">
                  {t('CalorieAllergySettingDetail.item_code')}:{' '}
                  {data?.process_item?.process_item_cd || data?.purchase_item.purchase_item_cd}
                </div>
                <div className="col">
                  {t('CalorieAllergySettingDetail.item_name')}：{' '}
                  {data?.process_item?.process_item_name || data?.purchase_item.purchase_item_name}
                </div>
                <div className="col">
                  {t('CalorieAllergySettingDetail.application_period')}：
                  {data?.process_item?.effective_start_date || data?.purchase_item.effective_start_date}&nbsp;～ &nbsp;
                  {data?.process_item?.effective_end_date || data?.purchase_item.effective_end_date}
                </div>
              </div>
              <div className="row">
                <div className="col">
                  {t('CalorieAllergySettingDetail.status')}: {data?.status}
                </div>
                <div className="col">
                  {t('CalorieAllergySettingDetail.last_update_by')}：{data?.applicant_name}
                </div>
                <div className="col">
                  {t('CalorieAllergySettingDetail.last_update_date')}：{data?.applied_date}
                </div>
                <div className="col">
                  {t('CalorieAllergySettingDetail.confirmed_by')} : {data?.verifier_name}
                </div>
                <div className="col">
                  {t('CalorieAllergySettingDetail.confirmed_date')}: {data?.verify_date}
                </div>
                <div className="col">
                  {t('CalorieAllergySettingDetail.approved_by')} : {data?.approver_name}
                </div>
                <div className="col">
                  {t('CalorieAllergySettingDetail.approved_date')}: {data?.approve_date}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* list */}
        <div className="mb-1 fw-bold">{t('CalorieAllergySettingDetail.nutrition_value_per_100g')}</div>
        <div className="table table-responsive mb-3">
          <div className="div-table fixsize-column purchase-detail-table">
            <div className="div-thead div-tr">
              {nutritions &&
                nutritions.map((nutrition, idx) => {
                  return (
                    <div
                      className="div-th d-flex align-items-center justify-content-center lh-1"
                      style={{ width: `${COLUMN_WIDTH[0]}rem` }}
                      key={idx}
                    >
                      {nutrition.nutrition_name} <br></br> ({nutrition.nutrition_unit_name})
                    </div>
                  );
                })}
            </div>
            <div className="div-tr">
              {nutritions &&
                nutritions.map((nutrition, idx) => {
                  const nutritionMaster =
                    data && data.purchase_nutrition_master.find((item) => item.nutrition_cd === nutrition.nutrition_cd);
                  return (
                    <div
                      className="div-td d-flex align-items-stretch justify-content-center lh-2"
                      style={{
                        width: `${COLUMN_WIDTH[1]}rem`,
                        background: nutritionMaster?.change_flg && data?.status !== APPROVED ? '#f8cecc' : '',
                      }}
                      key={idx}
                    >
                      <NutritionInputItem
                        text={
                          nutritionMaster?.nutrition_amount !== null && nutritionMaster?.nutrition_amount !== undefined
                            ? nutritionMaster.nutrition_amount.toString()
                            : ''
                        }
                        readonly={data?.status === PENDING_CONFIRMATION || data?.status === PENDING_APPROVAL}
                        onChange={(e) => handleChangeNutritionAmount(e)}
                        id={nutritionMaster?.id.toString() || uniqueId('nutrition-master-')}
                        errors={errors}
                        name={'nutrition_master_' + nutrition.nutrition_cd}
                        nutrition_cd={nutrition.nutrition_cd}
                      />
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
        {/* list */}
        <div className="mb-1 fw-bold">{t('CalorieAllergySettingDetail.specific_raw_materials')}</div>
        <AllergyTable
          rowsPerPage={10}
          columnWidth={COLUMN_WIDTH}
          purchaseItemAllergyMaster={data?.purchase_item_allergy_master}
          readonly={data?.status === PENDING_CONFIRMATION || data?.status === PENDING_APPROVAL}
          isApproved={data?.status === APPROVED}
          allergyUnitItems={allergyUnitItems}
          handleChangeAllergyItem={handleChangeAllergyItem}
          allergyType={SPECIFIC_RAW_MATERIALS}
        />
        <div className="mb-1 fw-bold">
          {t('CalorieAllergySettingDetail.items_equivalent_to_specific_raw_materials')}
        </div>

        <AllergyTable
          rowsPerPage={10}
          columnWidth={COLUMN_WIDTH}
          purchaseItemAllergyMaster={data?.purchase_item_allergy_master}
          readonly={data?.status === PENDING_CONFIRMATION || data?.status === PENDING_APPROVAL}
          isApproved={data?.status === APPROVED}
          allergyUnitItems={allergyUnitItems}
          handleChangeAllergyItem={handleChangeAllergyItem}
          allergyType={ITEMS_EQUIVALENT_TO_SPECIFIC_RAW_MATERIALS}
        />

        <div className="mb-1 fw-bold">{t('CalorieAllergySettingDetail.remark')}</div>
        <div className="row g-0 ">
          <div className="col-md-6 col-sm-12 bg-main p-1 text-center border">
            {t('CalorieAllergySettingDetail.table.remark')}
          </div>
        </div>
        <div className="row g-0">
          <div className="col-md-6 col-sm-12 border border-top-0">
            <textarea
              rows={4}
              onChange={handleChangeRemark}
              value={data?.process_item?.remarks || data?.purchase_item.remarks}
              disabled={data?.status === PENDING_CONFIRMATION || data?.status === PENDING_APPROVAL}
              className="form-control border-0"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PurchaseProcessedCalorieAllergyDetailSetting;
